import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import { ArrowRight } from "lucide-react";

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const BookTilt = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 120,
    damping: 16,
  });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), {
    stiffness: 120,
    damping: 16,
  });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      style={{ perspective: 1200 }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative flex justify-center"
    >
      <div
        aria-hidden="true"
        className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(0,102,255,0.22)_0%,rgba(10,15,29,0)_70%)] animate-blaulicht"
      />
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 40, rotateY: -14 }}
        animate={{ opacity: 1, y: 0, rotateY: 0 }}
        transition={{ duration: 1.2, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
      >
        <div
          aria-hidden="true"
          className="absolute -left-1.5 top-1 bottom-1 w-1.5 rounded-l-sm bg-alarm/70"
          style={{ transform: "translateZ(-2px)" }}
        />
        <img
          src="/assets/cover-vorderseite.png"
          alt="Buchcover BlaulichtPraxis – Datenschutz in der Blaulichtfamilie (Vorderseite)"
          data-testid="hero-book-cover"
          className="book-shadow relative w-full max-w-[320px] sm:max-w-[400px] rounded-r-md rounded-l-sm"
          fetchpriority="high"
        />
      </motion.div>
    </div>
  );
};

const Hero = () => {
  const { scrollYProgress } = useScroll();
  const yBook = useTransform(scrollYProgress, [0, 0.18], [0, -70]);

  return (
    <section
      id="start"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <div
        aria-hidden="true"
        className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,rgba(0,102,255,0.16)_0%,rgba(10,15,29,0)_70%)]"
      />
      <div
        aria-hidden="true"
        className="absolute top-1/3 -right-48 h-[640px] w-[640px] rounded-full bg-[radial-gradient(circle,rgba(0,209,255,0.1)_0%,rgba(10,15,29,0)_70%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blau/40 to-transparent"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 lg:gap-10 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-blau-glow"
            data-testid="hero-overline"
          >
            BlaulichtPraxis · Band 1
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6">
            <MaskedLine delay={0.15}>Datenschutz in der</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="text-blau text-glow-blue">Blaulicht</span>familie
            </MaskedLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
          >
            <p className="mt-6 text-base md:text-lg text-gray-300 font-medium">
              Praxiswissen für Feuerwehr, Rettungsdienst, Katastrophenschutz und
              THW
            </p>
            <p className="mt-4 text-base md:text-lg font-semibold text-white">
              Datenschutz im Einsatz: verstehen, richtig entscheiden, sicher
              handeln.
            </p>
            <p className="mt-4 text-sm sm:text-base text-gray-400 leading-relaxed max-w-xl">
              Datenschutz gehört längst zum Alltag von Einsatzorganisationen.
              Dieses Buch zeigt, wie sich die Anforderungen verständlich,
              praxisnah und strukturiert in Feuerwehr, Rettungsdienst, THW und
              Hilfsorganisationen umsetzen lassen.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <a
                href="#bestellen"
                data-testid="hero-cta-order"
                className="group inline-flex items-center gap-2 rounded-full bg-blau px-7 py-3.5 text-sm font-semibold text-white transition-[background-color,transform,box-shadow] duration-200 hover:bg-blau-glow hover:text-navy-950 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_-8px_rgba(0,102,255,0.8)]"
              >
                Buch bestellen
                <ArrowRight
                  size={16}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </a>
              <a
                href="#buch"
                data-testid="hero-cta-more"
                className="inline-flex items-center rounded-full border border-white/15 px-7 py-3.5 text-sm font-semibold text-gray-200 transition-[border-color,background-color] duration-200 hover:border-blau/60 hover:bg-blau/10"
              >
                Mehr über das Buch
              </a>
            </div>

            <p
              className="mt-8 font-mono text-xs uppercase tracking-[0.22em] text-gray-500"
              data-testid="hero-trustline"
            >
              Praxisnah <span className="text-blau mx-1">·</span> Verständlich{" "}
              <span className="text-blau mx-1">·</span> Strukturiert
            </p>
          </motion.div>
        </div>

        <motion.div style={{ y: yBook }}>
          <BookTilt />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
